import { useRef, useState } from 'react';
import { FolderOpen, LockKeyhole } from 'lucide-react';
import { Banner } from '@/components/Banner';
import { Button } from '@/components/Button';
import { Card, Emblema } from '@/components/Card';
import { Casilla } from '@/components/Casilla';
import { PasswordField } from '@/components/Field';
import {
  analizarRespaldo,
  analizarRespaldoConFotos,
  compararPerfil,
  esZip,
  fotosComoBlobs,
  nombrePerfil,
  restaurarRespaldo,
  type ComparacionPerfil,
  type RespaldoAnalizado,
} from '@/db/backup';
import { descifrarBytes, leerEnvolturaCifrada, type EnvolturaCifrada } from '@/db/backup-crypto';
import { espacioDeFotos } from '@/db/fotos';
import { hayDatosCapturados, leerSettings, totalRegistros } from '@/db/schema';
import { mensajeDeError } from '@/db/tx';
import type { Bytes } from '@/db/types';
import { describirUltimoRespaldo } from '@/domain/backup-reminder';
import { ErrorRespaldo } from '@/domain/errors';
import { leerArchivoComoBytes } from '@/lib/file-transfer';
import { VistaPrevia } from './VistaPrevia';
import { BotonDeshacer } from './BotonDeshacer';

interface Revision {
  analizado: RespaldoAnalizado;
  nombreArchivo: string;
  cifrado: boolean;
  comparacion: ComparacionPerfil;
  registrosTelefono: number;
  /** El teléfono no tiene cifrado y el respaldo sí: lo adopta al restaurar. */
  adoptaCifrado: boolean;
  /**
   * Los bytes de las fotos verificadas, si el archivo es un ZIP. null = es un
   * respaldo en JSON: no trae fotos y restaurarlo no toca las del teléfono
   * (design.md 10.7).
   */
  fotos: Map<string, Bytes> | null;
  /** Cuántas fotos tiene este teléfono ahora. */
  fotosDelTelefono: number;
}

type Estado =
  | { fase: 'inicio' }
  | { fase: 'leyendo' }
  | { fase: 'contrasena'; envoltura: EnvolturaCifrada; nombreArchivo: string }
  | { fase: 'vista'; revision: Revision }
  | { fase: 'confirmar'; revision: Revision }
  | { fase: 'restaurando' }
  | { fase: 'listo'; registros: number; fechaCopia: string; registrosCopia: number }
  | { fase: 'deshecho'; registros: number };

/**
 * Restaurar o solo revisar un archivo (Req. 6.5 a 6.11, 6.14, 6.15, 6.22).
 *
 *   elegir archivo → (contraseña si está cifrado) → vista previa → confirmar → restaurar
 *
 * Nada cambia hasta "Sí, reemplazar mis datos". Las confirmaciones reforzadas
 * son casillas que hay que marcar: perfil distinto con datos en el teléfono, y
 * archivo que no pasó la verificación.
 */
export function RestaurarRespaldo() {
  const [estado, setEstado] = useState<Estado>({ fase: 'inicio' });
  const [error, setError] = useState<string | null>(null);
  const [contrasena, setContrasena] = useState('');
  const [aceptaPerfil, setAceptaPerfil] = useState(false);
  const [aceptaRiesgo, setAceptaRiesgo] = useState(false);
  const entrada = useRef<HTMLInputElement>(null);

  function reiniciar() {
    setEstado({ fase: 'inicio' });
    setError(null);
    setContrasena('');
    setAceptaPerfil(false);
    setAceptaRiesgo(false);
    if (entrada.current !== null) entrada.current.value = '';
  }

  async function revisar(
    analizado: RespaldoAnalizado,
    nombreArchivo: string,
    cifrado: boolean,
    fotos: Map<string, Bytes> | null,
  ) {
    const settings = await leerSettings();
    const comparacion = compararPerfil(
      analizado.resumen.perfil,
      { id: settings.perfilId === '' ? null : settings.perfilId, nombre: settings.nombre },
      await hayDatosCapturados(),
    );
    setEstado({
      fase: 'vista',
      revision: {
        analizado,
        nombreArchivo,
        cifrado,
        comparacion,
        registrosTelefono: await totalRegistros(),
        adoptaCifrado:
          settings.cifradoRespaldo === null &&
          (analizado.datos.settings[0]?.cifradoRespaldo ?? null) !== null,
        fotos,
        fotosDelTelefono: (await espacioDeFotos()).fotos,
      },
    });
  }

  /**
   * Un respaldo con fotos es un ZIP y uno sin fotos es texto. Se distingue por
   * los primeros bytes del archivo, no por su nombre: renombrarlo no cambia lo
   * que trae dentro.
   */
  async function revisarBytes(bytes: Bytes, nombreArchivo: string, cifrado: boolean) {
    if (esZip(bytes)) {
      const deZip = await analizarRespaldoConFotos(bytes);
      await revisar(deZip.analizado, nombreArchivo, cifrado, deZip.fotos);
      return;
    }
    let texto: string;
    try {
      texto = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    } catch {
      throw new ErrorRespaldo(
        'Ese archivo no es un respaldo de DuoFit: no es texto ni un archivo con fotos. ' +
          'No se modificó ningún dato.',
      );
    }
    const envoltura = leerEnvolturaCifrada(texto);
    if (envoltura !== null) {
      setEstado({ fase: 'contrasena', envoltura, nombreArchivo });
      return;
    }
    await revisar(await analizarRespaldo(texto), nombreArchivo, cifrado, null);
  }

  async function elegir(archivo: File) {
    setError(null);
    setEstado({ fase: 'leyendo' });
    try {
      await revisarBytes(await leerArchivoComoBytes(archivo), archivo.name, false);
    } catch (e) {
      setEstado({ fase: 'inicio' });
      setError(mensajeDeError(e));
    }
  }

  async function abrirCifrado(envoltura: EnvolturaCifrada, nombreArchivo: string) {
    setError(null);
    setEstado({ fase: 'leyendo' });
    try {
      // Dentro puede ir el respaldo en JSON o el ZIP con fotos: la envoltura
      // no lo dice y se reconoce por sus primeros bytes (backup-zip.ts)
      const bytes = await descifrarBytes(envoltura, contrasena);
      setContrasena('');
      // El perfil se revisa después de descifrar (design.md, 10.9)
      await revisarBytes(bytes, nombreArchivo, true);
    } catch (e) {
      setEstado({ fase: 'contrasena', envoltura, nombreArchivo });
      setError(mensajeDeError(e));
    }
  }

  async function restaurar(revision: Revision) {
    setError(null);
    setEstado({ fase: 'restaurando' });
    try {
      const { registros } = await restaurarRespaldo(revision.analizado, {
        // Sin esto las fotos del teléfono no se tocan: un respaldo en JSON no
        // las trae y tampoco dice que no hubiera ninguna (design.md 10.7)
        ...(revision.fotos !== null ? { fotos: fotosComoBlobs(revision.fotos) } : {}),
      });
      setEstado({
        fase: 'listo',
        registros,
        fechaCopia: describirUltimoRespaldo(new Date().toISOString()),
        registrosCopia: revision.registrosTelefono,
      });
      setAceptaPerfil(false);
      setAceptaRiesgo(false);
      if (entrada.current !== null) entrada.current.value = '';
    } catch (e) {
      setEstado({ fase: 'confirmar', revision });
      setError(mensajeDeError(e));
    }
  }

  return (
    <div className="space-y-4">
      <input
        ref={entrada}
        type="file"
        accept="application/json,.json,application/zip,.zip"
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => {
          const archivo = e.target.files?.[0];
          if (archivo !== undefined) void elegir(archivo);
        }}
      />

      {(estado.fase === 'inicio' || estado.fase === 'listo' || estado.fase === 'deshecho') && (
        <Card>
          <Emblema icono={FolderOpen} />
          <h2 className="mb-2 mt-3 text-[22px] leading-tight tracking-[-0.6px] text-texto">
            Revisar un respaldo
          </h2>
          <p className="text-[13px] leading-relaxed text-texto-suave">
            Verás la fecha, el perfil y los registros antes de reemplazar datos. También sirve para
            solo revisar un archivo. Sirve el .json de siempre y el .zip con fotos, cifrados o no.
          </p>
          <div className="mt-5">
            <Button variante="secundario" onClick={() => entrada.current?.click()} icono={FolderOpen}>
              Elegir un archivo de respaldo
            </Button>
          </div>
        </Card>
      )}

      {estado.fase === 'leyendo' && (
        <Banner tono="info" titulo="Revisando el archivo">
          Se comprueba su contenido y su suma de verificación. No se modifica ningún dato.
        </Banner>
      )}

      {estado.fase === 'contrasena' && (
        <Card className="space-y-4">
          <p className="flex items-start gap-2.5 text-[13px] leading-relaxed text-texto">
            <LockKeyhole className="mt-0.5 size-[18px] text-marca" />
            <span>
              <strong>{estado.nombreArchivo}</strong> está cifrado. Sin su contraseña no se puede
              abrir: es la que tenías configurada cuando se creó.
            </span>
          </p>
          <PasswordField
            etiqueta="Contraseña del respaldo"
            valor={contrasena}
            onCambio={setContrasena}
            autoComplete="current-password"
          />
          <Button
            disabled={contrasena === ''}
            onClick={() => void abrirCifrado(estado.envoltura, estado.nombreArchivo)}
          >
            Abrir el respaldo
          </Button>
          <Button variante="secundario" onClick={reiniciar}>
            Cancelar
          </Button>
        </Card>
      )}

      {error !== null && (
        <Banner tono="alerta" titulo="No se pudo continuar">
          <p>{error}</p>
          <p className="mt-2 font-medium">Tus datos actuales no se modificaron.</p>
        </Banner>
      )}

      {estado.fase === 'vista' && (
        <>
          <VistaPrevia
            resumen={estado.revision.analizado.resumen}
            nombreArchivo={estado.revision.nombreArchivo}
            cifrado={estado.revision.cifrado}
          />
          <AvisoDeFotos revision={estado.revision} />
          <div className="space-y-2">
            {estado.revision.analizado.resumen.errores.length === 0 && (
              <Button onClick={() => setEstado({ fase: 'confirmar', revision: estado.revision })}>
                Restaurar este respaldo
              </Button>
            )}
            <Button variante="secundario" onClick={reiniciar}>
              Cerrar sin restaurar
            </Button>
          </div>
        </>
      )}

      {estado.fase === 'confirmar' && (
        <Confirmacion
          revision={estado.revision}
          aceptaPerfil={aceptaPerfil}
          onAceptaPerfil={setAceptaPerfil}
          aceptaRiesgo={aceptaRiesgo}
          onAceptaRiesgo={setAceptaRiesgo}
          onConfirmar={() => void restaurar(estado.revision)}
          onCancelar={reiniciar}
        />
      )}

      {estado.fase === 'restaurando' && (
        <Banner tono="info" titulo="Restaurando">
          Guardando una copia de tus datos actuales y reemplazándolos. No cierres la app.
        </Banner>
      )}

      {estado.fase === 'listo' && (
        <div className="space-y-2">
          <Banner tono="exito" titulo="Restauración completa">
            Se restauraron {estado.registros} registros. Revisa que todo esté bien; si no, puedes
            volver a los datos de antes.
          </Banner>
          <BotonDeshacer
            fechaCopia={estado.fechaCopia}
            registrosCopia={estado.registrosCopia}
            onDeshecho={(registros) => setEstado({ fase: 'deshecho', registros })}
          />
        </div>
      )}

      {estado.fase === 'deshecho' && (
        <Banner tono="exito" titulo="Restauración deshecha">
          Volviste a tus datos de antes: {estado.registros} registros.
        </Banner>
      )}
    </div>
  );
}

/**
 * Qué pasa con las fotos de progreso al restaurar (design.md 10.7). Son el
 * único dato con dos caminos, así que se dice siempre que haya algo en juego:
 *
 * - Archivo con fotos (.zip): las reemplaza, como todo lo demás.
 * - Archivo en JSON: no las trae y NO las borra. El teléfono las conserva.
 */
function AvisoDeFotos({ revision }: { revision: Revision }) {
  const cuantas = (n: number) => (n === 1 ? '1 foto' : `${n} fotos`);
  const enElTelefono = revision.fotosDelTelefono;
  const delArchivo = revision.fotos;

  if (delArchivo === null) {
    // Respaldo en JSON: ni las trae ni las borra
    if (enElTelefono === 0) return null;
    return (
      <Banner tono="info" titulo="Tus fotos de progreso no se tocan">
        Este respaldo está en JSON y no lleva fotos, así que tampoco las borra:{' '}
        {cuantas(enElTelefono)} de este iPhone se queda{enElTelefono === 1 ? '' : 'n'} como
        est{enElTelefono === 1 ? 'á' : 'án'}. Para respaldarlas, usa «Respaldo con fotos».
      </Banner>
    );
  }

  if (delArchivo.size === 0) {
    if (enElTelefono === 0) return null;
    return (
      <Banner tono="alerta" titulo="Este archivo con fotos no trae ninguna">
        Al restaurarlo, {cuantas(enElTelefono)} de este iPhone se borrará
        {enElTelefono === 1 ? '' : 'n'}. Si las quieres conservar, haz antes un «Respaldo con
        fotos».
      </Banner>
    );
  }

  return (
    <Banner tono="info" titulo="Este archivo trae tus fotos de progreso">
      Trae {cuantas(delArchivo.size)}, {delArchivo.size === 1 ? 'comprobada' : 'cada una comprobada'}{' '}
      contra su huella.
      {enElTelefono > 0 &&
        ` ${cuantas(enElTelefono)} de este iPhone se reemplazará${enElTelefono === 1 ? '' : 'n'} por ${delArchivo.size === 1 ? 'esa' : 'esas'}.`}
    </Banner>
  );
}

interface PropsConfirmacion {
  revision: Revision;
  aceptaPerfil: boolean;
  onAceptaPerfil: (valor: boolean) => void;
  aceptaRiesgo: boolean;
  onAceptaRiesgo: (valor: boolean) => void;
  onConfirmar: () => void;
  onCancelar: () => void;
}

/** Confirmación de una acción destructiva (Req. 8.10), reforzada si hace falta (6.7, 6.15). */
function Confirmacion({
  revision,
  aceptaPerfil,
  onAceptaPerfil,
  aceptaRiesgo,
  onAceptaRiesgo,
  onConfirmar,
  onCancelar,
}: PropsConfirmacion) {
  const { comparacion, analizado, registrosTelefono } = revision;
  const { resumen } = analizado;
  const pidePerfil = comparacion.tipo === 'distinto';
  const pideRiesgo = resumen.exigeConfirmacionReforzada;
  const habilitado = (!pidePerfil || aceptaPerfil) && (!pideRiesgo || aceptaRiesgo);

  return (
    <div className="space-y-3">
      <Banner tono="aviso" titulo="Se reemplazarán todos los datos de este teléfono">
        <p>
          Este teléfono tiene {registrosTelefono} registros y el archivo trae{' '}
          {resumen.totalRegistros}. Restaurar no combina: lo que no esté en el archivo desaparece.
        </p>
        <p className="mt-2">
          Antes de reemplazar se guarda una copia interna de tus datos actuales, para que puedas
          deshacerlo.
        </p>
      </Banner>

      <AvisoDeFotos revision={revision} />

      {comparacion.tipo === 'adopta' && (
        <Banner tono="info" titulo="Este teléfono tomará el perfil del respaldo">
          Como aquí todavía no hay datos, el teléfono queda con el perfil {nombrePerfil(resumen.perfil)}.
        </Banner>
      )}

      {revision.adoptaCifrado && (
        <Banner tono="info" titulo="El cifrado de respaldos quedará activado">
          El respaldo trae el cifrado activado: los próximos respaldos te pedirán la contraseña que
          tenías configurada cuando se hizo. Lo puedes cambiar o desactivar en Respaldos →
          Protección con contraseña.
        </Banner>
      )}

      {pidePerfil && (
        <div className="space-y-2">
          <Banner tono="alerta" titulo="El respaldo es de otro perfil">
            El archivo es de <strong>{comparacion.archivo}</strong> y este teléfono es de{' '}
            <strong>{comparacion.telefono}</strong>. Cada uno tiene sus propios datos.
          </Banner>
          <Casilla marcada={aceptaPerfil} onCambio={onAceptaPerfil}>
            Entiendo que los datos de {comparacion.telefono} se reemplazarán por los de{' '}
            {comparacion.archivo}.
          </Casilla>
        </div>
      )}

      {pideRiesgo && (
        <Casilla marcada={aceptaRiesgo} onCambio={onAceptaRiesgo}>
          Entiendo que el archivo pudo modificarse o dañarse, y quiero restaurarlo de todas formas.
        </Casilla>
      )}

      <div className="space-y-2">
        <Button variante="peligro" tamano="grande" disabled={!habilitado} onClick={onConfirmar}>
          Sí, reemplazar mis datos
        </Button>
        <Button variante="secundario" onClick={onCancelar}>
          Cancelar
        </Button>
      </div>
    </div>
  );
}
